/**
 * Item Sources Service
 *
 * CRUD for workspace_item_sources — the admin-configurable list of idea
 * submission sources. If a workspace has no rows yet, a fallback hardcoded
 * list is returned so the form always has options.
 */

import { getSupabaseClient } from './supabaseClient';

export interface WorkspaceItemSource {
  id: string;
  workspaceId: string;
  value: string;
  label: string;
  isActive: boolean;
  isBuiltin: boolean;
  sortOrder: number;
  createdAt: string;
  /**
   * 'idea' — where the idea came from (people pick it). 'arrival' — how the
   * item arrived (import, connector, queue, session…), set by itself when an
   * item arrives and pickable too (migration 926).
   */
  kind: 'idea' | 'arrival';
}

const TABLE = 'workspace_item_sources';


function rowToSource(row: Record<string, unknown>): WorkspaceItemSource {
  return {
    id: row.id as string,
    workspaceId: row.workspace_id as string,
    value: row.value as string,
    label: row.label as string,
    isActive: row.is_active as boolean,
    isBuiltin: row.is_builtin as boolean,
    sortOrder: row.sort_order as number,
    createdAt: row.created_at as string,
    kind: row.kind === 'arrival' ? 'arrival' : 'idea',
  };
}

/**
 * An item's source as words: its entry's label in the workspace list, or —
 * for a value the list doesn't hold — the value itself made readable.
 */
export function itemSourceLabel(value: string | null | undefined, sources: WorkspaceItemSource[]): string | null {
  if (!value) return null;
  const hit = sources.find((s) => s.value === value);
  if (hit) return hit.label;
  const words = value.replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Fetch all sources for a workspace, ordered by sort_order. */
export async function listItemSources(
  workspaceId: string
): Promise<{ data: WorkspaceItemSource[]; error: string | null }> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('sort_order', { ascending: true });

  if (error) return { data: [], error: error.message };

  if (!data || data.length === 0) {
    // No rows yet — seed the defaults and return the real persisted rows so
    // callers always operate on DB-backed IDs (never fake client-side IDs).
    const { error: seedError } = await supabase.rpc('seed_workspace_item_sources', {
      p_workspace_id: workspaceId,
    });
    if (seedError) return { data: [], error: seedError.message };

    const { data: seeded, error: refetchError } = await supabase
      .from(TABLE)
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('sort_order', { ascending: true });

    if (refetchError) return { data: [], error: refetchError.message };
    return { data: (seeded as Record<string, unknown>[]).map(rowToSource), error: null };
  }

  return { data: (data as Record<string, unknown>[]).map(rowToSource), error: null };
}

/** Fetch only active sources for a workspace (used in submission forms). */
export async function listActiveItemSources(
  workspaceId: string
): Promise<{ data: WorkspaceItemSource[]; error: string | null }> {
  const { data, error } = await listItemSources(workspaceId);
  return {
    data: data.filter((s) => s.isActive),
    error,
  };
}

/** Toggle is_active for a source. */
export async function setItemSourceActive(
  id: string,
  isActive: boolean
): Promise<{ error: string | null }> {
  const supabase = getSupabaseClient();
  const { error } = await supabase
    .from(TABLE)
    .update({ is_active: isActive })
    .eq('id', id);
  return { error: error?.message ?? null };
}

/** Update sort_order for a source. */
export async function updateItemSourceOrder(
  id: string,
  sortOrder: number
): Promise<{ error: string | null }> {
  const supabase = getSupabaseClient();
  const { error } = await supabase
    .from(TABLE)
    .update({ sort_order: sortOrder })
    .eq('id', id);
  return { error: error?.message ?? null };
}

/** Create a new custom source. */
export async function createItemSource(
  workspaceId: string,
  value: string,
  label: string,
  sortOrder: number
): Promise<{ data: WorkspaceItemSource | null; error: string | null }> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from(TABLE)
    .insert({ workspace_id: workspaceId, value, label, is_active: true, is_builtin: false, sort_order: sortOrder })
    .select('*')
    .single();
  if (error) return { data: null, error: error.message };
  return { data: rowToSource(data as Record<string, unknown>), error: null };
}

/** Update the label of a custom source. */
export async function updateItemSourceLabel(
  id: string,
  label: string
): Promise<{ error: string | null }> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.from(TABLE).update({ label }).eq('id', id);
  return { error: error?.message ?? null };
}

/** Delete a custom (non-builtin) source. */
export async function deleteItemSource(id: string): Promise<{ error: string | null }> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.from(TABLE).delete().eq('id', id).eq('is_builtin', false);
  return { error: error?.message ?? null };
}
